import React, { useState } from 'react';
import { useAuth } from '../data/auth';

/* ═══════════════════════════════════════════
   LOGIN PAGE — Clean, branded, login only
   Signup lives at /signup (SignupPage.js)
   ═══════════════════════════════════════════ */

export default function LoginPage() {
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Please fill in all fields"); return; }
    setLoading(true);
    setError("");
    const result = await auth.login(email, password);
    if (!result?.success && result?.error) setError(result.error);
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    setError("");
    await auth.loginWithGoogle();
  };

  const handleForgotPassword = async () => {
    if (!email) { setError("Enter your email above first"); return; }
    setLoading(true);
    await auth.resetPassword(email);
    setResetSent(true);
    setLoading(false);
  };

  const goSignup = () => {
    window.history.pushState({}, "", "/signup");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  // Styles
  const cardStyle = {
    width: 420, background: "#2C2E33", borderRadius: 12, border: "1px solid rgba(217,219,221,0.10)",
    padding: "32px 36px", boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
  };
  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)",
    background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 13, outline: "none",
    fontFamily: "inherit", boxSizing: "border-box", transition: "border-color 0.15s",
  };
  const btnPrimary = {
    width: "100%", padding: "12px 0", borderRadius: 8, border: "none",
    background: "#4059AD", color: "#FAFAFA", fontSize: 13, fontWeight: 400,
    cursor: "pointer", fontFamily: "inherit", transition: "opacity 0.15s",
  };
  const btnOutline = {
    width: "100%", padding: "12px 0", borderRadius: 8,
    border: "1px solid rgba(217,219,221,0.12)", background: "transparent",
    color: "#FAFAFA", fontSize: 13, fontWeight: 400, cursor: "pointer", fontFamily: "inherit",
  };
  const linkStyle = { color: "#4059AD", background: "none", border: "none", cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 };

  return (
    <div style={{ minHeight: "100vh", background: "#1E1F23", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 24, padding: "40px 20px" }}>
      <style>{`
        input::placeholder { color: rgba(250,250,250,0.25); }
        input:focus { border-color: rgba(64,89,173,0.5) !important; }
      `}</style>

      {/* Logo */}
      <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ height: 22, objectFit: "contain", marginBottom: 8 }} />

      <div style={cardStyle}>
        <div style={{ fontSize: 18, fontWeight: 400, color: "#FAFAFA", marginBottom: 4 }}>Welcome back</div>
        <div style={{ fontSize: 12, color: "rgba(250,250,250,0.4)", marginBottom: 24 }}>Log in to your CoreFlow account</div>

        <form onSubmit={handleLogin}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ fontSize: 11, color: "rgba(250,250,250,0.4)", display: "block", marginBottom: 6 }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: "rgba(250,250,250,0.4)", display: "block", marginBottom: 6 }}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" style={inputStyle} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setShowForgot(true)} style={linkStyle}>Forgot password?</button>
            </div>
            {error && <div style={{ fontSize: 12, color: "#E53935", padding: "8px 12px", background: "rgba(229,57,53,0.08)", borderRadius: 8 }}>{error}</div>}
            <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }}>
              {loading ? "Signing in..." : "Log In"}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0" }}>
              <div style={{ flex: 1, height: 1, background: "rgba(217,219,221,0.08)" }} />
              <span style={{ fontSize: 11, color: "rgba(250,250,250,0.25)" }}>or</span>
              <div style={{ flex: 1, height: 1, background: "rgba(217,219,221,0.08)" }} />
            </div>
            <button type="button" onClick={handleGoogleLogin} style={{ ...btnOutline, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <svg width="16" height="16" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>
          </div>
        </form>

        <div style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "rgba(250,250,250,0.4)" }}>
          Don't have an account? <button onClick={goSignup} style={{ ...linkStyle, fontSize: 13 }}>Sign up</button>
        </div>
      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ ...cardStyle, width: 380 }}>
            <div style={{ fontSize: 16, fontWeight: 400, color: "#FAFAFA", marginBottom: 8 }}>Reset Password</div>
            {resetSent ? (
              <div>
                <div style={{ fontSize: 13, color: "rgba(250,250,250,0.5)", lineHeight: 1.6, marginBottom: 16 }}>If an account exists for {email}, you'll receive a reset link.</div>
                <button onClick={() => { setShowForgot(false); setResetSent(false); }} style={btnPrimary}>Got it</button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, color: "rgba(250,250,250,0.5)", lineHeight: 1.6, marginBottom: 16 }}>Enter your email and we'll send a reset link.</div>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" style={{ ...inputStyle, marginBottom: 14 }} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setShowForgot(false)} style={{ ...btnOutline, flex: 1 }}>Cancel</button>
                  <button onClick={handleForgotPassword} disabled={loading} style={{ ...btnPrimary, flex: 1 }}>{loading ? "Sending..." : "Send Reset Link"}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: "rgba(250,250,250,0.2)", marginTop: 8 }}>
        © {new Date().getFullYear()} CoreFlow. All rights reserved.
      </div>
    </div>
  );
}
